import React from 'react';
import { format } from 'date-fns';
import { decode } from 'he';
import { CheckCircle, MoreVertical } from 'lucide-react';
import { Topic } from '../../types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { resolveDisplayMediaUrl } from '../../utils/mediaUrl';

interface TopicCardProps {
  topic: Topic;
  onClick: () => void;
}

export const TopicCard: React.FC<TopicCardProps> = ({ topic, onClick }) => {
  const isDraggingRef = React.useRef(false);

  // Process the image URL if it contains wp-content/uploads/
  const processImageUrl = (url: string | undefined): string => {
    const resolved = resolveDisplayMediaUrl(url);
    if (resolved && resolved.includes('wp-content/uploads/')) {
      return resolved.split('?')[0] + '?resize=350%2C233';
    }
    return resolved;
  };

  // Strip HTML tags from text
  const stripHtml = (html: string): string => {
    if (!html) return '';
    const decoded = decode(html);
    // Remove HTML tags using regex
    return decoded.replace(/<[^>]*>/g, '').trim();
  };

  // Get excerpted summary that fits in a single line
  const getExcerptedSummary = (summary: string, maxLength: number = 50): string => {
    const stripped = stripHtml(summary);
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength).trim() + '...';
  };

  // Parse keywords from comma-separated string into array
  const parseKeywords = (keywords: string | undefined): string[] => {
    if (!keywords) return [];
    return keywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);
  };

  const handleDragStart = (e: React.DragEvent) => {
    isDraggingRef.current = true;
    // Store the topic data in the drag event
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify(topic));

    // Create a custom drag image with image on top and text below
    const dragImage = document.createElement('div');
    dragImage.style.width = '200px';
    dragImage.style.height = '160px';
    dragImage.style.padding = '8px';
    dragImage.style.backgroundColor = '#fff';
    dragImage.style.border = '1px solid #ccc';
    dragImage.style.borderRadius = '4px';
    dragImage.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
    dragImage.style.opacity = '0.9';
    dragImage.style.display = 'flex';
    dragImage.style.flexDirection = 'column';
    dragImage.style.gap = '8px';
    dragImage.style.overflow = 'hidden';
    dragImage.style.fontFamily = 'inherit';
    dragImage.style.boxSizing = 'border-box';

    // Add thumbnail image on top
    const imgContainer = document.createElement('div');
    imgContainer.style.width = '100%';
    imgContainer.style.height = '100px';
    imgContainer.style.flexShrink = '0';
    imgContainer.style.borderRadius = '4px';
    imgContainer.style.overflow = 'hidden';
    imgContainer.style.backgroundColor = '#f0f0f0';

    const img = document.createElement('img');
    const processedUrl = resolveDisplayMediaUrl(
      topic.mediaUrl?.includes('wp-content/uploads/')
        ? topic.mediaUrl.split('?')[0] + '?resize=350%2C233'
        : topic.mediaUrl
    );
    img.src = processedUrl || '';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    imgContainer.appendChild(img);
    dragImage.appendChild(imgContainer);

    // Add text content below
    const textContainer = document.createElement('div');
    textContainer.style.display = 'flex';
    textContainer.style.flexDirection = 'column';
    textContainer.style.gap = '4px';
    textContainer.style.flex = '1';
    textContainer.style.minWidth = '0';
    textContainer.style.overflow = 'hidden';

    // Title
    const title = document.createElement('div');
    title.textContent = decode(topic.title || '');
    title.style.fontSize = '12px';
    title.style.fontWeight = '600';
    title.style.lineHeight = '1.3';
    title.style.color = '#333';
    title.style.display = '-webkit-box';
    title.style.webkitLineClamp = '2';
    title.style.webkitBoxOrient = 'vertical';
    title.style.overflow = 'hidden';
    textContainer.appendChild(title);

    // Publish date
    const publishDate = document.createElement('div');
    const date = new Date(topic.publishedDate);
    publishDate.textContent = format(date, "dd/MM/yy 'at' h:mm a");
    publishDate.style.fontSize = '10px';
    publishDate.style.color = '#666';
    publishDate.style.lineHeight = '1.2';
    textContainer.appendChild(publishDate);

    dragImage.appendChild(textContainer);

    // Position off-screen and append to body
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    dragImage.style.left = '-1000px';
    document.body.appendChild(dragImage);

    // Set as drag image (offset to center)
    e.dataTransfer.setDragImage(dragImage, 100, 80);

    // Clean up after a short delay
    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage);
      }
    }, 0);
  };

  const keywords = parseKeywords(topic.keywords);

  return (
    <div className="bg-core-dark rounded overflow-hidden border border-cmp-line-2 relative group">
      <div
        draggable
        onDragStart={handleDragStart}
        onDragEnd={() => {
          // Reset after a short delay to allow click to be processed if it wasn't a drag
          setTimeout(() => {
            isDraggingRef.current = false;
          }, 100);
        }}
        onClick={(e) => {
          // Only trigger onClick if we didn't just drag and didn't click on menu
          if (!isDraggingRef.current && !(e.target as HTMLElement).closest('[data-menu-trigger]')) {
            onClick();
          }
          isDraggingRef.current = false;
        }}
        className="cursor-grab active:cursor-grabbing flex flex-col h-full rounded overflow-hidden relative hover:[&_.media-container]:opacity-90"
      >
        {topic.alreadyPosted && (
          <CheckCircle className="absolute top-2 right-2 text-cmp-bronze w-6 h-6 z-10 bg-cmp-raise/90 rounded-full p-0.5" />
        )}

        {/* Media thumbnail on top */}
        <div
          className="media-container relative w-full transition-opacity duration-300 bg-cmp-surface"
          style={{ paddingTop: '66.67%' }}
        >
          {topic.mediaType === 'VIDEO' ? (
            <video
              src={processImageUrl(topic.mediaUrl)}
              className="absolute top-0 left-0 w-full h-full object-cover"
            />
          ) : (
            <img
              src={processImageUrl(topic.mediaUrl)}
              alt={topic.title}
              className="absolute top-0 left-0 w-full h-full object-cover"
            />
          )}
        </div>

        {/* Content below image */}
        <div className="p-3 bg-core-dark flex flex-col gap-2 relative">
          {/* Title and summary group with tight spacing */}
          <div className="flex flex-col gap-0.5">
            {/* Title (like filename) */}
            <p className="text-xs text-cmp-ink font-medium leading-tight line-clamp-1 overflow-hidden">
              {decode(topic.title || '')}
            </p>

            {/* Summary excerpt (like file size) - single line */}
            <p className="text-[0.625rem] text-cmp-ink-2 leading-tight line-clamp-1 overflow-hidden">
              {getExcerptedSummary(topic.summary || '')}
            </p>
          </div>

          {/* Keyword pills */}
          {keywords.length > 0 && (
            <div className="flex gap-1.5 overflow-hidden">
              {keywords.map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded-cmp1 text-xs font-cmp-mono bg-cmp-surface text-cmp-ink-2 border border-cmp-line whitespace-nowrap flex-shrink-0"
                >
                  {keyword}
                </span>
              ))}
            </div>
          )}

          {/* Action menu icon in bottom right */}
          <div className="absolute bottom-2 right-2" data-menu-trigger>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <button
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-cmp-bronze-soft"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="w-4 h-4 text-cmp-ink-2" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem>View Details</DropdownMenuItem>
                <DropdownMenuItem>Edit</DropdownMenuItem>
                <DropdownMenuItem className="text-red-600">Delete</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
};
